import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from '../../http.service';
import { buildPeriodOptions, granularityOf, PeriodOption } from './period-resolver';

@Component({
  selector: 'app-agent-account',
  templateUrl: './agent-account.component.html',
  styleUrls: ['./agent-account.component.scss'],
})
export class AgentAccountComponent implements OnInit {
  public period: any = { label: '', from_date: '', to_date: '' };
  public periodOptions: PeriodOption[] = [];
  public selectedPeriodKey: string = '';
  public defaultPeriodKey: string = '';
  public filter: any = { from_date: '', to_date: '' };
  public agents: any[] = [];
  public loading = false;
  public company_uuid: string = '';
  public bucketRows: any[] = [];
  public bucketTotal: number = 0;
  public bucketLoading = false;

  get memberCommissionTotal(): number {
    return this.agents.reduce((total, agent) => total + Number(agent.total_commission || 0), 0);
  }

  get reportRowTotal(): number {
    return this.agents.reduce((total, agent) => total + Number(agent.row_count || 0), 0);
  }

  get earningMemberCount(): number {
    return this.agents.filter(agent => Number(agent.total_commission || 0) > 0).length;
  }

  get totalCommissionPool(): number {
    return this.memberCommissionTotal + Number(this.bucketTotal || 0);
  }

  bucketGameBreakdown(bucket: any): any[] {
    return Array.isArray(bucket?.game_breakdown) ? bucket.game_breakdown : [];
  }

  get selectedPeriodLabel(): string {
    const opt = this.periodOptions.find(o => o.key === this.selectedPeriodKey);
    return opt ? opt.label : 'Custom Range';
  }

  constructor(private http: HttpService, private route: ActivatedRoute, private router: Router) {}

  async ngOnInit() {
    this.company_uuid = this.http.getStaticSelectedCompany();
    await this.initPeriodFilter();
    await this.load();
  }

  async initPeriodFilter() {
    const s: any = await this.http.post('/admin/setting/get');
    const setting = s?.agent_account_period || 'current_month';
    this.periodOptions = buildPeriodOptions(granularityOf(setting), 12, new Date());
    const preselect = this.periodOptions.find(o => o.key === setting) || this.periodOptions[0];
    this.defaultPeriodKey = preselect.key;
    this.selectedPeriodKey = preselect.key;
    this.filter.from_date = preselect.from_date;
    this.filter.to_date = preselect.to_date;
  }

  onPeriodChange(key: string) {
    if (key === 'custom') {
      this.selectedPeriodKey = key;
      return;
    }
    const opt = this.periodOptions.find(o => o.key === key);
    if (!opt) return;
    this.selectedPeriodKey = key;
    this.filter.from_date = opt.from_date;
    this.filter.to_date = opt.to_date;
    this.load();
  }

  search() {
    this.selectedPeriodKey = 'custom';
    this.load();
  }

  reset() {
    const opt = this.periodOptions.find(o => o.key === this.defaultPeriodKey) || this.periodOptions[0];
    if (!opt) return;
    this.selectedPeriodKey = opt.key;
    this.filter.from_date = opt.from_date;
    this.filter.to_date = opt.to_date;
    this.load();
  }

  async load() {
    this.loading = true;
    try {
      const res: any = await this.http.post('/admin/agent-account/list', {
        from_date: this.filter.from_date,
        to_date: this.filter.to_date,
        period_label: this.selectedPeriodLabel,
      });
      if (res?.status) {
        this.period = res.period;
        this.agents = res.agents || [];
        this.bucketRows = res.bucket_rows || [];
        this.bucketTotal = Number(res.bucket_total || 0);
      } else {
        this.agents = [];
        this.bucketRows = [];
        this.bucketTotal = 0;
      }
    } finally {
      this.loading = false;
    }
  }

  drill(agent: any) {
    this.router.navigate(['/lo', this.company_uuid, 'referral-rolling-account', agent.agent_code]);
  }

  // Open the member's seamless wallet page (topup/withdraw + referral history log tab).
  openMember(agent: any) {
    if (!agent?.player_uuid) return;
    this.router.navigate(
      ['/lo', this.company_uuid, 'wallet-topup-withdraw'],
      { queryParams: { player: agent.player_uuid } }
    );
  }
}
